// ─────────────────────────────────────────────────────────────────────────────
// Photography registry
// ─────────────────────────────────────────────────────────────────────────────
// One entry per real Five Lights Reno photograph. Everything on the site that
// shows a photo pulls it from here, so a caption and alt text can never drift
// away from what the picture actually shows.
//
// GROUND RULES
//  1. Every image in this file is a genuine Five Lights Reno job-site photo.
//     No stock, no library imagery, no "representative" substitutes. The one
//     stock kitchen photo the old site carried has been deleted rather than
//     re-captioned — see `KITCHEN PHOTO GAP` at the bottom.
//  2. `caption` describes what is literally in the frame. Where a photo is
//     mid-build rather than finished, the caption says so — work in progress
//     is the most convincing thing a contractor can show, as long as it isn't
//     passed off as a finished room.
//  3. `alt` is written for a screen-reader user who cannot see the image, and
//     never repeats the caption verbatim.
//  4. `ratio` is the source aspect ratio. These are phone photos: 1:1 or 4:5.
//     Frames are chosen to match so nothing is crushed into a letterbox crop.

import basementBedroom from "../Images/Portfolio/Basement/basement-finished-bedroom.jpg";
import basementHallway from "../Images/Portfolio/Basement/basement-finished-hallway.jpg";
import bathGlassDoor from "../Images/Portfolio/Bath/bath-glass-shower-door.jpg";
import bathMarbleDetail from "../Images/Portfolio/Bath/bath-marble-shower-detail.jpg";
import bathVanityMirror from "../Images/Portfolio/Bath/bath-vanity-mirror-shower.jpg";
import exteriorBrickGarage from "../Images/Portfolio/Exterior/exterior-brick-home-garage.jpg";
import flooringVinylPlank from "../Images/Portfolio/Flooring/flooring-vinyl-plank-basement.jpg";
import laundryStacked from "../Images/Portfolio/Laundry/laundry-stacked-washer-dryer.jpg";
import insulationFraming from "../Images/Portfolio/Process/insulation-framing-detail.jpg";
import tileLevelingClips from "../Images/Portfolio/Process/tile-wall-install-leveling-clips.jpg";
import staircaseAfter from "../Images/Portfolio/Staircase/staircase-after-stained.jpg";
import staircaseBefore from "../Images/Portfolio/Staircase/staircase-before-raw-wood.jpg";
import staircaseMasking from "../Images/Portfolio/Staircase/staircase-masking-prep.jpg";
import staircaseInProgress from "../Images/Portfolio/Staircase/staircase-refinishing-in-progress.jpg";
import tileEntryway from "../Images/Portfolio/Tile/tile-entryway-threshold.jpg";
import tileFeatureWall from "../Images/Portfolio/Tile/tile-feature-wall-marble-slat.jpg";
import tileFloorLargeFormat from "../Images/Portfolio/Tile/tile-floor-large-format.jpg";

export const MEDIA = {
  staircaseAfter: {
    src: staircaseAfter,
    ratio: "4 / 5",
    caption: "Staircase rebuilt with stained treads over painted risers",
    alt: "A staircase with dark stained wood treads, white painted risers, black iron spindles and a panelled white wall alongside it.",
  },
  staircaseBefore: {
    src: staircaseBefore,
    ratio: "4 / 5",
    caption: "The same staircase stripped back to bare pine treads",
    alt: "The same staircase before refinishing, with raw untreated pine treads, no spindles, and a temporary metal handrail.",
  },
  staircaseInProgress: {
    src: staircaseInProgress,
    ratio: "4 / 5",
    caption: "Sanding the treads with the stairwell sheeted off",
    alt: "A worker sanding stair treads part-way through the job, with plastic sheeting taped across the stairwell to contain dust.",
  },
  staircaseMasking: {
    src: staircaseMasking,
    ratio: "4 / 5",
    caption: "Masking and dust containment before any sanding starts",
    alt: "Stair treads and surrounding trim covered in masking paper and tape ahead of sanding work.",
  },

  bathVanityMirror: {
    src: bathVanityMirror,
    ratio: "4 / 5",
    caption: "Shower walls tiled and waterproofed, before the glass went in",
    alt: "A shower alcove tiled floor to ceiling in large-format marble-look porcelain, with a waterproofed black shower base and centre drain not yet tiled.",
  },
  bathGlassDoor: {
    src: bathGlassDoor,
    ratio: "4 / 5",
    caption: "The finished shower — sliding glass on a hexagon mosaic base",
    alt: "A completed walk-in shower with a sliding glass door, marble-look porcelain walls, brushed nickel fixtures and a white hexagon mosaic tile floor.",
  },
  bathMarbleDetail: {
    src: bathMarbleDetail,
    ratio: "4 / 5",
    caption: "Where the wall tile meets the mosaic floor and the drain sits flush",
    alt: "Close view of a shower interior showing the junction between large marble-look wall tiles, hexagon mosaic floor tile and a flush square drain.",
  },

  basementBedroom: {
    src: basementBedroom,
    ratio: "4 / 5",
    caption: "Basement bedroom finished out — egress window, trim, plank floor",
    alt: "An empty finished basement bedroom with pale grey walls, white trim and door, a below-grade egress window, and grey vinyl plank flooring.",
  },
  basementHallway: {
    src: basementHallway,
    ratio: "4 / 5",
    caption: "The connecting hallway on the same basement build-out",
    alt: "A finished basement hallway with two white panel doors, fresh baseboard, flush ceiling lights and grey vinyl plank flooring running the length of it.",
  },
  insulationFraming: {
    src: insulationFraming,
    ratio: "4 / 5",
    caption: "The same basement earlier: framing, batt insulation, vapour barrier",
    alt: "A basement wall under construction, showing timber studs, mineral wool batt insulation and a taped polyethylene vapour barrier with an outlet box sealed into it.",
  },

  tileFloorLargeFormat: {
    src: tileFloorLargeFormat,
    ratio: "1 / 1",
    caption: "Large-format porcelain going down over a levelled subfloor",
    alt: "Two large marble-look porcelain floor tiles set into fresh adhesive at a doorway, held flat by red levelling clips, with notched adhesive and a bucket beside them.",
  },
  tileEntryway: {
    src: tileEntryway,
    ratio: "1 / 1",
    caption: "Entryway threshold where the tile meets the adjoining floor",
    alt: "An entryway floor tiled in large marble-look porcelain, meeting the threshold of an adjoining room.",
  },
  tileLevelingClips: {
    src: tileLevelingClips,
    ratio: "4 / 5",
    caption: "Wall tile held true with levelling clips while the adhesive sets",
    alt: "Wall tiles being installed with plastic levelling clips between them to keep the faces flush while the adhesive cures.",
  },
  tileFeatureWall: {
    src: tileFeatureWall,
    ratio: "1 / 1",
    caption: "Marble-look slabs and a slat-wood panel on a living-area wall",
    alt: "A feature wall clad in book-matched marble-look porcelain slabs next to a vertical slat-wood panel, with a recessed black reveal running along the base.",
  },

  flooringVinylPlank: {
    src: flooringVinylPlank,
    ratio: "4 / 5",
    caption: "Vinyl plank run wall to wall through a lower level",
    alt: "Grey vinyl plank flooring installed across a finished lower-level room, with white baseboard fitted along the walls.",
  },

  laundryStacked: {
    src: laundryStacked,
    ratio: "4 / 5",
    caption: "Laundry nook built to fit a stacked washer and dryer",
    alt: "A stacked white washer and dryer fitted into a purpose-built alcove with marble-look tile flooring and trim carried through from the next room.",
  },

  exteriorBrickGarage: {
    src: exteriorBrickGarage,
    ratio: "1 / 1",
    caption: "Front door, garage doors, bay surround and gable trim refinished in black",
    alt: "The front of a two-storey red brick house with both garage doors, the front door, the bay window surround and the gable trim all refinished in black.",
  },
};

// ── KITCHEN PHOTO GAP ───────────────────────────────────────────────────────
// The business offers kitchen renovations, but no photograph of a completed
// Five Lights Reno kitchen has been supplied. The previous site filled the gap
// with a stock photo of a black-cabinet kitchen; that file has been deleted.
//
// Until a real one exists, the kitchen service page and the kitchen row in the
// service index run without a hero photograph rather than borrowing an image
// of somebody else's work. `services.js` marks that service `photo: null` and
// the components handle it — see components/sections/ServiceIndex.jsx.
//
// One phone photo of a finished kitchen closes this.

// Ordered list used by anything that wants "all photography" — e.g. the
// projects page fallback grid.
export const MEDIA_ORDER = [
  "staircaseAfter",
  "bathGlassDoor",
  "basementHallway",
  "tileFeatureWall",
  "basementBedroom",
  "bathVanityMirror",
  "exteriorBrickGarage",
  "flooringVinylPlank",
  "tileFloorLargeFormat",
  "laundryStacked",
  "bathMarbleDetail",
  "tileEntryway",
  "insulationFraming",
  "staircaseInProgress",
  "tileLevelingClips",
  "staircaseBefore",
  "staircaseMasking",
];
